import clsx from 'clsx'
import React from 'react'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'

type FilterBadgeProps = {
    text: string
    showRemove?: boolean
    onRemove?: () => void
    className?: string
    title?: string
}

type FilterBadgeConfig = {
    color: BadgeColor
    removeLabelPrefix: string
}

function FilterBadge({
    text,
    showRemove = false,
    onRemove,
    className,
    title,
    color,
    removeLabelPrefix,
}: FilterBadgeProps & FilterBadgeConfig): React.JSX.Element {
    const removeAriaLabel = `${removeLabelPrefix} "${text}"`

    return (
        <Badge
            color={color}
            className={clsx('text-sm', className)}
            borderless={true}
            title={title ?? text}
            rightIcon={
                showRemove ? (
                    <button
                        type="button"
                        aria-label={removeAriaLabel}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold leading-none text-inherit hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1"
                        onClick={(event) => {
                            event.stopPropagation()
                            onRemove?.()
                        }}
                    >
                        ×
                    </button>
                ) : undefined
            }
        >
            <span className="block max-w-[240px] truncate">{text}</span>
        </Badge>
    )
}

export function IncludeJobTitleBadge(
    props: FilterBadgeProps,
): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.BrandBlue}
            removeLabelPrefix="Remove job title"
        />
    )
}

export function ExcludeJobTitleBadge(
    props: FilterBadgeProps,
): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Error}
            removeLabelPrefix="Remove excluded job title"
        />
    )
}

export function IncludeCompanyBadge(
    props: FilterBadgeProps,
): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Success}
            removeLabelPrefix="Remove company"
        />
    )
}

export function ExcludeCompanyBadge(
    props: FilterBadgeProps,
): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Error}
            removeLabelPrefix="Remove excluded company"
        />
    )
}

export function KeywordGroupBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.SkyBlue}
            removeLabelPrefix="Remove keyword group"
        />
    )
}

export function SeniorityBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Cyan}
            removeLabelPrefix="Remove seniority"
        />
    )
}

export function JobFunctionBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Violet}
            removeLabelPrefix="Remove job function"
        />
    )
}

export function TrustedCircleBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.BrandBlue}
            removeLabelPrefix="Remove trusted circle contact"
        />
    )
}

export function LocationBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.Neutral2}
            removeLabelPrefix="Remove location"
        />
    )
}

export function ExperienceBadge(props: FilterBadgeProps): React.JSX.Element {
    return (
        <FilterBadge
            {...props}
            color={BadgeColor.InfoBlue}
            removeLabelPrefix="Remove experience filter"
        />
    )
}
